import {
  toValue,
  watchEffect,
  getCurrentScope,
  type MaybeRefOrGetter,
} from "vue";
import type Ably from "ably";
import {
  CHANNELS,
  useAblyChannelRegistry,
} from "@/composables/ably/ably-channel-registry";
import { defaultAblyChannelTopic } from "@/composables/ably/ably.types";
import type {
  IChatMessagePayload,
  IChatMessageStatusUpdateWithPayload,
  IChatTranslationEvent,
} from "@f3/shared/chat-types";
import type { IChatMessage } from "../model/chat-model";
import type { ChatMessageLikesAggregateDelta } from "@/features/show/services/sockets/sockets-service.types";

const SEEN_IDS_MAX = 200;

/**
 * Pure filtering helper. Returns true when the message should be processed.
 * Rejects wrong roomId and already-seen message IDs.
 * When accepted, records the id in seenIds with FIFO eviction at SEEN_IDS_MAX.
 */
export function filterChatMessage(
  msg: IChatMessagePayload,
  roomId: string,
  seenIds: Map<string, true>,
): boolean {
  if (msg.chat_room_id !== roomId) return false;
  if (seenIds.has(msg.id)) return false;
  if (seenIds.size >= SEEN_IDS_MAX) {
    const oldest = seenIds.keys().next().value;
    if (oldest !== undefined) seenIds.delete(oldest);
  }
  seenIds.set(msg.id, true);
  return true;
}

export const chatRealtimeServiceLogPrefix = "[ChatRealtimeService] ";

export interface ChatMessageRemoveEvent {
  type: "removed";
  chat_message_id: string;
  event_by_id: string | null;
}

export interface ChatMessagePinUpdateEvent {
  type: "pin-updated";
  chat_message_id: string | null;
  event_by_id: string | null;
}

export type ChatMessageEvent =
  | ChatMessageRemoveEvent
  | ChatMessagePinUpdateEvent
  | IChatMessageStatusUpdateWithPayload
  | IChatTranslationEvent
  | ChatMessageLikesAggregateDelta;

type ChatMessageActionEvent =
  | ChatMessageRemoveEvent
  | ChatMessagePinUpdateEvent
  | IChatMessageStatusUpdateWithPayload;

export enum ChatMessageEventListenerType {
  ADD = "add",
  REMOVE = "remove",
  PIN_UPDATE = "pin-update",
}

export function useChatRealtime(
  roomId: MaybeRefOrGetter<string | null | undefined>,
  chatEventsChannelArgs:
    | { contextType: "show"; id: string }
    | { contextType: "channel"; id: string }
    | MaybeRefOrGetter<string>,
) {
  const seenIds = new Map<string, true>();
  const registry = useAblyChannelRegistry();

  // Support both legacy string channel name (computed) and new structured args.
  // The structured form feeds CHANNELS.chatEvents directly; the string form is
  // kept for callers that pass a pre-built channel name.
  let chatHandle: ReturnType<typeof registry.subscribeChannel>;
  if (
    typeof chatEventsChannelArgs === "object" &&
    "contextType" in chatEventsChannelArgs
  ) {
    chatHandle = registry.subscribeChannel({
      descriptor: CHANNELS.chatEvents as any,
      channelArgs: [
        chatEventsChannelArgs.contextType,
        chatEventsChannelArgs.id,
      ] as any,
      scope: getCurrentScope() ?? undefined,
    });
  } else {
    // Legacy: caller passes a computed/ref string channel name.
    // Build a one-off descriptor wrapping the raw name.
    const rawName = toValue(chatEventsChannelArgs as MaybeRefOrGetter<string>);
    // Determine contextType and id from the channel name pattern: fanfest-chat:{id}:chat-events
    const parts = rawName.split(":");
    const id = parts[1] ?? rawName;
    chatHandle = registry.subscribeChannel({
      descriptor: CHANNELS.chatEvents as any,
      channelArgs: ["show", id] as any,
      scope: getCurrentScope() ?? undefined,
    });
  }

  function watchChatMessages(
    listener: (
      data: { id: string | null; payload?: IChatMessagePayload },
      event: ChatMessageEventListenerType,
      eventById?: string,
    ) => void,
    translationListener?: (event: IChatTranslationEvent) => void,
    likesAggregateListener?: (delta: ChatMessageLikesAggregateDelta) => void,
  ) {
    function listenerWrapper(msg: Ably.Message) {
      const data = msg.data as
        | ChatMessageEvent
        | IChatMessage
        | IChatMessagePayload
        | ChatMessageLikesAggregateDelta[];

      // Like aggregates can arrive as an array when the BE publisher flushes
      // more than one pending messageId update in the same publish.
      if (Array.isArray(data)) {
        for (const entry of data) {
          if (entry.type === "chat-message-likes") {
            likesAggregateListener?.(entry);
          }
        }
        return;
      }

      if ("type" in data) {
        if (data.type === "chat-translation") {
          translationListener?.(data as IChatTranslationEvent);
          return;
        }

        if (data.type === "chat-message-likes") {
          likesAggregateListener?.(data as ChatMessageLikesAggregateDelta);
          return;
        }

        let eventType: ChatMessageEventListenerType;

        if (data.type === "removed") {
          eventType = ChatMessageEventListenerType.REMOVE;
        } else if (data.type === "pin-updated") {
          eventType = ChatMessageEventListenerType.PIN_UPDATE;
        } else {
          eventType = ChatMessageEventListenerType.ADD;
        }

        const payload =
          data.type === "status-update"
            ? (data as IChatMessageStatusUpdateWithPayload).payload
            : undefined;

        const actionEvent = data as ChatMessageActionEvent;
        listener(
          { id: actionEvent.chat_message_id, payload },
          eventType,
          actionEvent.event_by_id ?? undefined,
        );
      } else if ("id" in data && "chat_room_id" in data) {
        const payload = data as IChatMessagePayload;
        const currentRoomId = toValue(roomId);
        if (
          currentRoomId &&
          filterChatMessage(payload, currentRoomId, seenIds)
        ) {
          listener(
            { id: payload.id, payload },
            ChatMessageEventListenerType.ADD,
          );
        }
      } else if ("id" in data) {
        listener(
          { id: (data as IChatMessage).id },
          ChatMessageEventListenerType.ADD,
        );
      } else {
        if (import.meta.env.DEV) {
          console.log(
            chatRealtimeServiceLogPrefix +
              "Ignoring chat message, invalid event type",
            data,
          );
        }
      }
    }

    return watchEffect((onCleanup) => {
      const unsub = chatHandle.subscribe(
        defaultAblyChannelTopic,
        listenerWrapper,
      );
      onCleanup(() => {
        unsub();
      });
    });
  }

  function sendChatMessage(data: IChatMessage) {
    void chatHandle.publish(defaultAblyChannelTopic, data);
  }

  function sendChatMessageRemove(messageId: string, eventById: string) {
    void chatHandle.publish(defaultAblyChannelTopic, {
      type: "removed",
      chat_message_id: messageId,
      event_by_id: eventById,
    } as ChatMessageRemoveEvent);
  }

  function sendChatMessagePinUpdate(
    messageId: string | null,
    eventById: string,
  ) {
    void chatHandle.publish(defaultAblyChannelTopic, {
      type: "pin-updated",
      chat_message_id: messageId,
      event_by_id: eventById,
    } as ChatMessagePinUpdateEvent);
  }

  return {
    watchChatMessages,
    sendChatMessage,
    sendChatMessageRemove,
    sendChatMessagePinUpdate,
  };
}
